import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  FolderHeart,
  Pause,
  Play,
  ScanLine,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Store,
} from "lucide-react";

const scenes = [
  {
    id: "binders",
    label: "Build Binders",
    title: "From fresh pull to organized collection.",
    description:
      "Scan and add cards inside your Binder. Organize your favorites and follow their available value history.",
    href: "/virtual-binder",
    action: "Open My Binders",
    icon: FolderHeart,
    steps: ["Scan your card", "Confirm the exact match", "Add to your Binder"],
  },
  {
    id: "ai",
    label: "Use AI Tools",
    title: "A little guidance for your next big chase.",
    description:
      "Get collection ideas from AI Coach, or try guided photo capture for grading assistance.",
    href: "/ai-tools",
    action: "Explore AI Tools",
    icon: Sparkles,
    steps: ["Choose your collection", "Set your goal", "Explore AI guidance"],
  },
  {
    id: "shops",
    label: "Create a Shop",
    title: "Give your collection a place to shine.",
    description:
      "Introduce yourself, customize your collector shop, and showcase the cards you have available.",
    href: "/seller/shop",
    action: "Create your shop",
    icon: Store,
    steps: [
      "Make it your own",
      "Add your available cards",
      "Share your storefront",
    ],
  },
  {
    id: "trade",
    label: "Buy & Sell",
    title: "Your next grail. Someone else's next chapter.",
    description:
      "Browse available listings or sell a card from your Binder. Keep purchases and selling activity connected.",
    href: "/marketplace",
    action: "Explore the Marketplace",
    icon: ShoppingBag,
    steps: [
      "Find an available card",
      "Review listing details",
      "Buy or create a listing",
    ],
  },
];

function Preview({ scene }: { scene: string }) {
  return (
    <div className={`feature-screen screen-${scene}`} aria-hidden="true">
      <div className="preview-topbar">
        <span className="preview-dots">● ● ●</span>
        <span>
          POKELLAMA /{" "}
          {scene === "binders"
            ? "MY BINDERS"
            : scene === "ai"
              ? "AI TOOLS"
              : scene === "shops"
                ? "COLLECTOR SHOP"
                : "MARKETPLACE"}
        </span>
        <span>Illustrative preview</span>
      </div>
      {scene === "binders" ? (
        <div className="binder-demo">
          <div className="demo-heading">
            <div>
              <small>MY COLLECTION</small>
              <h3>The grail Binder</h3>
            </div>
            <span className="demo-pill">
              <ScanLine size={15} /> Scan & Add
            </span>
          </div>
          <div className="demo-card-grid">
            {[0, 1, 2].map((i) => (
              <div key={i} className={`demo-collectible collectible-${i}`}>
                <span>✦</span>
                <div className="card-orb" />
                <small>
                  {["THE CHASE", "THE FAVORITE", "THE FRESH PULL"][i]}
                </small>
              </div>
            ))}
          </div>
          <div className="demo-status">
            <Check size={17} /> Exact match confirmed → Added to Binder
          </div>
        </div>
      ) : scene === "ai" ? (
        <div className="ai-demo">
          <div className="ai-avatar">
            <Sparkles size={34} />
          </div>
          <small>COLLECTION COACH</small>
          <h3>
            What's your next
            <br />
            collection goal?
          </h3>
          <div className="demo-prompt">Help me finish my favorite set.</div>
          <div className="ai-answer">
            <Sparkles size={18} />
            <div>
              <strong>Let's explore your missing cards.</strong>
              <p>
                Review set gaps, available evidence, and options that fit your
                budget.
              </p>
            </div>
          </div>
        </div>
      ) : scene === "shops" ? (
        <div className="shop-demo">
          <div className="shop-banner">
            <img
              src="/home/rocket-llama.webp"
              alt=""
              width="600"
              height="600"
            />
            <div>
              <small>COLLECTOR SHOP</small>
              <h3>To the Moon Cards</h3>
              <p>Big pulls. Bigger collector energy.</p>
            </div>
          </div>
          <div className="shop-label">
            <strong>Available inventory</strong>
            <span>About · Shipping · Reviews</span>
          </div>
          <div className="shop-inventory">
            {["Fresh pulls", "Binder favorites", "Chase cards"].map((s, i) => (
              <div key={s}>
                <div className={`inventory-art inventory-${i}`}>✦</div>
                <span>{s}</span>
              </div>
            ))}
          </div>
          <div className="demo-status">
            <Store size={17} /> Your shop. Your story. Your collection.
          </div>
        </div>
      ) : (
        <div className="trade-demo">
          <div className="trade-card">
            <div className="demo-collectible collectible-2">
              <span>✦</span>
              <div className="card-orb" />
              <small>YOUR NEXT GRAIL</small>
            </div>
          </div>
          <div className="trade-detail">
            <small>FROM ANOTHER COLLECTOR</small>
            <h3>
              A new home
              <br />
              for a favorite.
            </h3>
            <div className="trade-row">
              <ShieldCheck size={18} />
              <span>Review card details & photos</span>
            </div>
            <div className="trade-row">
              <ShoppingBag size={18} />
              <span>Check price & shipping</span>
            </div>
            <div className="demo-pill">
              Browse available listings <ArrowUpRight size={15} />
            </div>
            <p>Or list a card from your Binder.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FeatureShowcase() {
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const region = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      setReducedMotion(preference.matches);
      setPlaying(!preference.matches);
    };
    update();
    preference.addEventListener("change", update);
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.2 },
    );
    if (region.current) observer.observe(region.current);
    const visibility = () =>
      setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", visibility);
    return () => {
      preference.removeEventListener("change", update);
      observer.disconnect();
      document.removeEventListener("visibilitychange", visibility);
    };
  }, []);
  const running = playing && visible && pageVisible;
  useEffect(() => {
    if (!running) return;
    const timer = window.setTimeout(
      () => setSelected((value) => (value + 1) % scenes.length),
      7000,
    );
    return () => window.clearTimeout(timer);
  }, [running, selected]);
  const scene = scenes[selected];
  return (
    <div
      ref={region}
      className={`feature-showcase ${running ? "is-playing" : "is-paused"} ${reducedMotion ? "reduced-motion" : ""}`}
    >
      <div className="feature-controls">
        <div
          className="feature-selector"
          role="group"
          aria-label="Choose a feature preview"
        >
          {scenes.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={selected === i}
              onClick={() => {
                setSelected(i);
                setPlaying(false);
              }}
            >
              <item.icon size={17} />
              {item.label}
            </button>
          ))}
        </div>
        <button
          className="preview-play"
          type="button"
          onClick={() => setPlaying((value) => !value)}
          aria-label={
            playing ? "Pause feature previews" : "Play feature previews"
          }
        >
          {playing ? <Pause size={17} /> : <Play size={17} />}
          <span>{playing ? "Pause" : "Play"}</span>
        </button>
      </div>
      <div className="feature-content">
        <div key={scene.id} className="preview-stage">
          <Preview scene={scene.id} />
          <div className="preview-progress">
            <span key={`${scene.id}-${running}`} />
          </div>
        </div>
        <div className="feature-description">
          <p className="home-eyebrow">0{selected + 1} / THE PLATFORM TOUR</p>
          <h3>{scene.title}</h3>
          <p>{scene.description}</p>
          <ol>
            {scene.steps.map((step, i) => (
              <li key={step}>
                <span>{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
          <Link className="home-button dark" to={scene.href}>
            {scene.action}
            <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
      <p className="preview-caption">
        Short animated feature previews · Illustrative screens, not live account
        data.
      </p>
    </div>
  );
}
